import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MessageCircle, Smartphone, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { whatsappLink, smsLink } from '@/lib/buyerMessaging';

export function SmsQueue({ campaign, onClose }) {
  const [q, setQ] = useState(null);
  const load = useCallback(() => API.get(`/campaigns/${campaign.id}/sms-queue`).then(r => setQ(r.data)), [campaign.id]);
  useEffect(() => { load(); }, [load]);
  const send = async (s, method) => {
    const win = method === 'whatsapp' ? window.open('', '_blank') : null;
    try {
      await API.post(`/campaigns/${campaign.id}/sms-log`, { send_id: s.id, method });
      const url = method === 'whatsapp' ? whatsappLink(s.to, s.message) : smsLink(s.to, s.message);
      if (win) win.location.href = url; else window.location.href = url;
      load();
    } catch (e) { win?.close(); toast.error(e.response?.data?.detail || 'Blocked'); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="bg-white max-w-xl max-h-[88vh] overflow-y-auto" data-testid="sms-queue-dialog">
        <DialogHeader><DialogTitle>Text Queue — {campaign.name}</DialogTitle></DialogHeader>
        {q?.quiet_hours && <p className="text-xs flex items-center gap-1.5 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-md px-3 py-2" data-testid="quiet-hours-banner"><Moon className="h-3.5 w-3.5" /> Quiet hours — texting is allowed 8am–8pm Eastern.</p>}
        {(q?.items || []).map(s => (
          <div key={s.id} data-testid={`sms-send-${s.id}`} className={`p-3 rounded-lg border ${s.status === 'texted' ? 'bg-emerald-50/60 border-emerald-200' : s.status === 'excluded' ? 'bg-gray-50 border-gray-200 opacity-70' : 'border-gray-200'}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-gray-900 truncate">{s.name} <span className="text-gray-400 font-normal">· {s.entity_name}</span></p><p className="text-[11px] text-gray-500">{s.to || s.reason}</p></div>
              {s.status === 'pending' ? <>
                <Button size="sm" data-testid={`sms-wa-${s.id}`} onClick={() => send(s, 'whatsapp')} className="bg-[#25D366] hover:bg-[#1ebe5b] text-white h-8"><MessageCircle className="h-3.5 w-3.5 mr-1" />WhatsApp</Button>
                <Button size="sm" data-testid={`sms-sms-${s.id}`} onClick={() => send(s, 'sms')} className="bg-gray-900 text-white h-8"><Smartphone className="h-3.5 w-3.5 mr-1" />SMS</Button>
              </> : <Badge variant="outline" className="text-[10px]">{s.status === 'texted' ? `Texted (${s.via})` : `Excluded: ${s.reason}`}</Badge>}
            </div>
            {s.status !== 'excluded' && <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">{s.message}</p>}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

